import React, { ChangeEvent, Component, FocusEvent, KeyboardEvent, RefObject } from 'react';
import styled from 'styled-components';

export const simpleId = () => {
  return `_${Math.random().toString(36).substr(2, 9)}`;
};

export const BACKSPACE_KEY = 8;

export const LEFT_ARROW_KEY = 37;

export const RIGHT_ARROW_KEY = 39;

const E_KEY = 69;

const Wrapper = styled.div`
  display: flex;
  box-shadow: 0 1px 4px 0 ${(props) => props.theme.palette.greyBorder.main};
  width: 100%;
  border-radius: 6px;

  &:hover {
    box-shadow: 0 4px 8px 0 ${(props) => props.theme.palette.greyBorder.main};
  }
`;

export const SingleInput = styled.input`
  font-family: monospace;
  border: none;
  border-radius: 0;
  text-align: center;
  width: ${(props) => `calc(100% / ${props.maxLength})`};
  height: 75px;
  /* Use static px value to avoid some mobile browsers from zooming in when getting focus */
  font-size: 16px;
  box-sizing: border-box;
  background-color: #fff;
  /* appearance is non-standard and needs manual prefixing. Needed to override default input styles in mobile browsers */
  /* stylelint-disable-next-line */
  -webkit-appearance: none;
  /* stylelint-disable-next-line */
  -moz-appearance: none;

  &:first-child {
    border-bottom-left-radius: 6px;
    border-top-left-radius: 6px;
  }
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  &:not(:last-child) {
    border-right: 1px solid ${(props) => props.theme.palette.greySoft.main};
  }
  &:focus {
    outline: none;
    border: 1px solid ${(props) => props.theme.palette.primary.main};
  }

  @media ${(props) => props.theme.device.tablet} {
    font-size: ${(props) => props.theme.sizes.large};
    text-align: center;
  }
`;

export type InputMode = 'none' | 'text' | 'decimal' | 'numeric' | 'tel';

export type PseudoEventTarget = {
  name: string;
  value: string;
};

export type PseudoInputEvent = {
  target: PseudoEventTarget;
};

export type PinCodeInputProps = {
  id?: string;
  type?: string;
  inputs: number;
  name: string;
  onChange?: (e: PseudoInputEvent) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  forceUppercase?: boolean;
  filterChars?: string[];
  defaultValue?: string;
  autoComplete?: 'one-time-code' | 'off';
  inputFilterFn?: (char: string) => boolean;
  style?: React.CSSProperties;
  inputMode?: InputMode;
};

function focusAndSelect(target: HTMLInputElement) {
  target.focus();
  target.select();
}

type PinCodeState = {
  input: string[];
};

export class PinCodeInput extends Component<PinCodeInputProps, PinCodeState> {
  textInput: RefObject<HTMLInputElement>[] = [];

  uuid = '';

  constructor(props: PinCodeInputProps) {
    super(props);

    const { inputs = 4, forceUppercase } = props;
    const { defaultValue = '' } = props;
    let value = defaultValue;
    const input = [];

    if (forceUppercase) {
      value = defaultValue.toUpperCase();
    }

    for (let i = 0; i < Number(inputs); i++) {
      const val = value[i] || '';
      input.push(val);
    }

    this.state = {
      input,
    };

    this.uuid = simpleId();

    this.textInput = Array(inputs)
      .fill(inputs)
      .map(() => React.createRef());
  }

  reportChange = (value: string) => {
    const { name, onChange } = this.props;

    if (onChange) {
      onChange({
        target: {
          name,
          value,
        },
      });
    }
  };

  handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    let value = String(target.value);
    const { type, inputs, inputFilterFn, forceUppercase } = this.props;

    if (forceUppercase) {
      value = value.toUpperCase();
    }

    if (type === 'number') {
      value = value.replace(/[^\d]/g, '');
    }

    /** Filter Chars */
    if (inputFilterFn) {
      value = value.split('').filter(inputFilterFn).join('');
    }

    let fullValue = value;

    if (value !== '') {
      const input = this.state.input.slice();

      if (value.length > 1) {
        value.split('').map((chart, i) => {
          if (Number(target.dataset.id) + i < inputs) {
            input[Number(target.dataset.id) + i] = chart;
          }

          return false;
        });
      } else {
        input[Number(target.dataset.id)] = value;
      }

      const targetIndex = target.dataset.id || 0;
      const maxIndex = inputs - 1;
      let nextIndex = Number(targetIndex) + value.length;
      nextIndex = nextIndex >= maxIndex ? maxIndex : nextIndex;
      const newTarget = this.textInput[nextIndex];

      if (newTarget && newTarget.current) {
        focusAndSelect(newTarget.current);
      }

      fullValue = input.join('');

      this.setState(() => {
        return {
          input,
        };
      });
    }

    this.reportChange(fullValue);
  };

  handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { currentTarget } = e;
    const { type } = this.props;
    const targetIndex = Number(currentTarget.dataset.id);
    const nextTarget = this.textInput[targetIndex + 1];
    const prevTarget = this.textInput[targetIndex - 1];

    if (e.metaKey && e.key === 'v') {
      // Pasting, will trigger an onChange
      return;
    }

    if (e.shiftKey && e.keyCode === LEFT_ARROW_KEY) {
      // User is selecting
      return;
    }

    let input;
    let value;

    switch (e.keyCode) {
      case BACKSPACE_KEY:
        e.preventDefault();
        input = this.state.input.slice();
        input[targetIndex] = '';
        value = input.join('');

        this.setState({ input });

        if (prevTarget && prevTarget.current) {
          focusAndSelect(prevTarget.current);
        }
        this.reportChange(value);
        break;

      case LEFT_ARROW_KEY:
        e.preventDefault();

        if (prevTarget && prevTarget.current) {
          focusAndSelect(prevTarget.current);
        }
        break;

      case RIGHT_ARROW_KEY:
        e.preventDefault();

        if (nextTarget && nextTarget.current) {
          focusAndSelect(nextTarget.current);
        }
        break;

      case E_KEY: // This case needs to be handled because of https://stackoverflow.com/questions/31706611/why-does-the-html-input-with-type-number-allow-the-letter-e-to-be-entered-in
        if (type === 'number') {
          e.preventDefault();
        }
        break;

      default:
        break;
    }
  };

  handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  render() {
    const {
      autoFocus = false,
      inputs,
      onChange,
      defaultValue,
      error,
      style = {},
      id,
      type = 'text',
      autoComplete = 'off',
      ...rest
    } = this.props;
    const { input = [] } = this.state;
    const componentId = id || this.uuid;

    return (
      <Wrapper style={style}>
        {input.map((v: string, i: number) => {
          return (
            <SingleInput
              ref={this.textInput[i]}
              id={`${componentId}-${i}`}
              data-testid={`${componentId}-${i}`}
              data-id={i}
              autoFocus={autoFocus && i === 0}
              value={v}
              /* eslint-disable-next-line react/no-array-index-key */
              key={`input_${i}`}
              min={0}
              max={9}
              maxLength={inputs}
              autoComplete={autoComplete}
              onFocus={this.handleFocus}
              onChange={this.handleChange}
              onKeyDown={this.handleKeyDown}
              data-valid={!!error}
              {...rest}
              type={type}
            />
          );
        })}
      </Wrapper>
    );
  }
}
